import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { StatsOverrideDialog } from "@/components/StatsOverrideDialog";
import { compressImageToDataURL } from "@/lib/image";
import {
  ArrowLeft, Mail, Phone, IdCard, Calendar, User as UserIcon,
  ShieldCheck, MessageSquare, Cake, BadgeCheck, Pencil, BarChart3,
  Camera, Loader2, Trash2, Maximize2,
} from "lucide-react";

function formatDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  } catch { return s; }
}

function InfoRow({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0" data-testid={testid}>
      <div className="h-9 w-9 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center text-muted-foreground flex-shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{label}</div>
        <div className="text-sm mt-0.5 break-words">{value || <span className="text-muted-foreground italic">Not provided</span>}</div>
      </div>
    </div>
  );
}

export default function AdminMemberDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  // Which slice of the profile the current dialog session is editing.
  // "contact"  → email + phone only
  // "personal" → DOB + gender
  // "bio"      → bio only
  // null       → full profile (legacy)
  const [editMode, setEditMode] = useState(null);
  const openEdit = (mode) => { setEditMode(mode); setEditOpen(true); };
  // Admin-avatar controls
  const avatarFileRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  // Avatar-rescale slider state.
  // Click the avatar to open a Popover with a Shadcn Slider — the value
  // live-updates ``sizePx`` so the admin sees the circle grow/shrink
  // in real time, and after they stop dragging we debounce-save the
  // chosen size to the member record so every viewer picks it up.
  const AVATAR_MIN = 64;
  const AVATAR_MAX = 256;
  const AVATAR_DEFAULT = 96;
  const [sizeOpen, setSizeOpen] = useState(false);
  const [sizePx, setSizePx] = useState(AVATAR_DEFAULT);
  const sizeSaveTimer = useRef(null);
  const lastSavedSize = useRef(AVATAR_DEFAULT);
  // Career stats aggregate (leaderboards row) + any admin override.
  // Kept together as ``stats`` because the endpoint already merges the
  // override into the returned row.
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsEditOpen, setStatsEditOpen] = useState(false);

  // Only "member managers" (plus the primary admin) may edit the stats
  // override — everyone else just reads.
  const canEditStats = !!me && (me.role === "primary_admin" || me.is_member_manager === true);

  // The stats endpoint keys on ``player_key`` (lowercase full name).
  const playerKey = useMemo(() => {
    if (!user) return "";
    const fn = (user.first_name || "").trim().toLowerCase();
    const ln = (user.last_name || "").trim().toLowerCase();
    return `${fn} ${ln}`.trim();
  }, [user]);
  const playerName = useMemo(() => {
    if (!user) return "";
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }, [user]);

  const loadStats = async (key) => {
    if (!key) { setStats(null); return; }
    setStatsLoading(true);
    try {
      const { data } = await api.get("/playhq/leaderboards");
      const row = (data?.players || []).find((x) => x.player_key === key);
      setStats(row || null);
    } catch (e) {
      // Silent — stats card just shows "no data yet" if this fails.
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const u = await api.get(`/members/${id}`);
        if (alive) setUser(u.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load member");
        if (alive) setUser(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  // Re-fetch stats whenever the resolved player_key changes.
  useEffect(() => {
    if (playerKey) loadStats(playerKey);
  }, [playerKey]);

  /* ---------- Avatar: pick / rescale / delete (admin) ---------- */
  const persistAvatar = async (avatar_url) => {
    if (!user) return;
    setAvatarBusy(true);
    // Optimistic — surface the change everywhere the second the admin
    // hits Change/Delete, roll back if the network call fails.
    const previous = user.avatar_url;
    setUser((u) => u ? { ...u, avatar_url } : u);
    try {
      const { data } = await api.put(`/admin/members/${user.id}/profile`, { avatar_url });
      setUser(data);
      toast.success(avatar_url ? "Photo updated" : "Photo removed");
    } catch (err) {
      setUser((u) => u ? { ...u, avatar_url: previous } : u);
      toast.error(err?.response?.data?.detail || "Photo save failed");
    } finally {
      setAvatarBusy(false);
    }
  };

  /* ---------- Avatar rescale (admin) ---------- */
  // Whenever the loaded user changes, sync our local size state to the
  // persisted value (falling back to the default when unset).
  useEffect(() => {
    const persisted = Number(user?.avatar_size_px) || AVATAR_DEFAULT;
    const clamped = Math.max(AVATAR_MIN, Math.min(AVATAR_MAX, persisted));
    setSizePx(clamped);
    lastSavedSize.current = clamped;
  }, [user?.id, user?.avatar_size_px]);

  // Debounced persistence — we save 500ms after the admin stops moving
  // the slider so we don't spam the API on every pixel change.
  const scheduleSizeSave = (val) => {
    if (sizeSaveTimer.current) clearTimeout(sizeSaveTimer.current);
    sizeSaveTimer.current = setTimeout(async () => {
      if (!user) return;
      if (val === lastSavedSize.current) return;
      try {
        const { data } = await api.put(`/admin/members/${user.id}/profile`, { avatar_size_px: val });
        lastSavedSize.current = val;
        // Keep the rest of the user record fresh but preserve our
        // in-flight slider position (data.avatar_size_px == val).
        setUser(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not save avatar size");
      }
    }, 500);
  };

  const onSizeChange = (arr) => {
    const v = Array.isArray(arr) ? arr[0] : arr;
    const clamped = Math.max(AVATAR_MIN, Math.min(AVATAR_MAX, Number(v) || AVATAR_DEFAULT));
    setSizePx(clamped);
    scheduleSizeSave(clamped);
  };

  const onAvatarFile = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (f.type !== "image/png" && !f.name.toLowerCase().endsWith(".png")) { toast.error("Only PNG images are allowed."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10MB before compression"); return; }
    setAvatarBusy(true);
    try {
      const dataUrl = await compressImageToDataURL(f, { maxDim: 512 });
      await persistAvatar(dataUrl);
    } catch {
      toast.error("Could not process this image");
      setAvatarBusy(false);
    }
  };

  const onAvatarDelete = () => {
    if (!user?.avatar_url) return;
    if (!window.confirm("Remove this member's photo?")) return;
    persistAvatar("");
  };

  // Accept Ctrl/Cmd+V pastes anywhere on the page as an avatar upload,
  // but skip the paste when the user is actively typing into another
  // field so copy/paste inside form inputs still works normally.
  // The pasted image flows through the same compress → persistAvatar
  // pipeline as a file-picker upload.
  useEffect(() => {
    if (!user) return;
    const handlePaste = async (e) => {
      // Don't hijack pastes aimed at text/input/contenteditable fields
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
      const items = e.clipboardData?.items || [];
      let imgFile = null;
      for (const it of items) {
        if (it.kind === "file" && (it.type || "").startsWith("image/")) {
          imgFile = it.getAsFile();
          break;
        }
      }
      if (!imgFile) return;
      e.preventDefault();
      if (imgFile.type !== "image/png" && !(imgFile.name || "").toLowerCase().endsWith(".png")) { toast.error("Only PNG images are allowed."); return; }
      if (imgFile.size > 10 * 1024 * 1024) { toast.error("Max 10MB before compression"); return; }
      setAvatarBusy(true);
      try {
        const dataUrl = await compressImageToDataURL(imgFile, { maxDim: 512 });
        await persistAvatar(dataUrl);
      } catch {
        toast.error("Could not process this image");
        setAvatarBusy(false);
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
    // persistAvatar closes over user via setUser functional update, so
    // we only need to re-bind when the active user id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (loading) {
    return (
      <AppShell variant="admin" title="Member profile">
        <Card className="p-6 rounded-2xl">
          <div className="flex items-center gap-4">
            <Skeleton className="h-20 w-20 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        </Card>
      </AppShell>
    );
  }

  if (!user) {
    return (
      <AppShell variant="admin" title="Member profile">
        <Card className="p-10 rounded-2xl text-center border-dashed">
          <div className="text-muted-foreground">Member not found.</div>
          <Button variant="outline" className="mt-4 rounded-full" onClick={() => nav("/a/members")}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to members
          </Button>
        </Card>
      </AppShell>
    );
  }

  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Unnamed member";
  const initials = ((user.first_name || "?")[0] + (user.last_name || "")[0] || "?").toUpperCase();

  return (
    <AppShell
      variant="admin"
      title="Member profile"
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => nav("/a/members")} data-testid="member-detail-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to members
          </Button>
        </div>
      }
    >
      {/* Header card — accent-tinted hero to match the club's PlayHQ /
          member-portal cards. Uses design tokens (--accent, --bg) so it
          auto-flips in dark mode, plus a subtle blurred circle in the
          corner for depth without breaking the gradient budget. */}
      <Card
        className="p-6 rounded-2xl overflow-hidden relative bg-[hsl(var(--accent))] text-[hsl(var(--bg))] border-transparent"
        data-testid="member-detail-header"
      >
        {/* Decorative depth accents (kept subtle — well under the 20% gradient budget) */}
        <div aria-hidden className="pointer-events-none absolute -right-16 -bottom-24 w-80 h-80 rounded-full bg-white/15 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -right-10 -top-24 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[hsl(var(--bg))]" data-testid="member-detail-name">{fullName}</h2>
              {user.status === "approved" && (
                <Badge className="bg-white/15 text-[hsl(var(--bg))] border-white/25 hover:bg-white/20">
                  <BadgeCheck className="h-3.5 w-3.5 mr-1" /> Approved
                </Badge>
              )}
            </div>
            <div className="mt-2 text-sm text-[hsl(var(--bg))]/85 flex items-center gap-3 flex-wrap">
              {user.member_id && (
                <span className="inline-flex items-center gap-1.5 font-mono-sm">
                  <IdCard className="h-3.5 w-3.5" /> {user.member_id}
                </span>
              )}
              {user.membership_type && (
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5" /> {user.membership_type}
                </span>
              )}
              {user.age != null && (
                <span className="inline-flex items-center gap-1.5">
                  <Cake className="h-3.5 w-3.5" /> {user.age} years
                </span>
              )}
            </div>
            <div className="mt-3 text-xs text-[hsl(var(--bg))]/70 inline-flex items-center gap-1.5">
              <Pencil className="h-3 w-3" /> More admin tools coming soon
            </div>
          </div>

          {/* Photo drop / upload box.
              Sits on the right of the hero on desktop, stacks below the
              name block on mobile. Clicking or dropping a file fires the
              existing persistAvatar() flow (client-side compression to
              512px → PUT /admin/members/{id}/profile → optimistic
              update everywhere). The trash pill deletes the current
              photo. */}
          <div className="w-full sm:w-40 md:w-44 lg:w-48 shrink-0" data-testid="member-detail-photo-box">
            <button
              type="button"
              onClick={() => !avatarBusy && avatarFileRef.current?.click()}
              disabled={avatarBusy}
              className="group relative w-full aspect-square rounded-2xl overflow-hidden bg-white/10 hover:bg-white/15 border-2 border-dashed border-white/30 hover:border-white/60 focus:outline-none focus:ring-2 focus:ring-white/60 transition-colors disabled:opacity-70"
              title={user.avatar_url ? "Replace photo (auto-shrunk to 512px)" : "Upload photo (auto-shrunk to 512px)"}
              data-testid="member-detail-photo-upload"
            >
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={fullName}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="member-detail-photo"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-[hsl(var(--bg))]/90 px-3 text-center">
                  {avatarBusy ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <>
                      <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center mb-2">
                        <Camera className="h-5 w-5" />
                      </div>
                      <div className="text-xs font-semibold">Add photo</div>
                      <div className="text-[10px] opacity-80 mt-0.5">Click or paste image</div>
                    </>
                  )}
                </div>
              )}
              {/* Overlay on hover when there's already a photo */}
              {user.avatar_url && !avatarBusy && (
                <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white">
                  <Camera className="h-5 w-5 mb-1" />
                  <div className="text-xs font-semibold">Replace</div>
                  <div className="text-[10px] opacity-80 mt-0.5">Click or paste</div>
                </div>
              )}
              {avatarBusy && user.avatar_url && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-white" />
                </div>
              )}
            </button>
            {user.avatar_url && !avatarBusy && (
              <button
                type="button"
                onClick={onAvatarDelete}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs rounded-full py-1.5 bg-white/10 hover:bg-red-500/25 text-[hsl(var(--bg))]/90 hover:text-white border border-white/20 hover:border-red-500/40 transition-colors"
                data-testid="member-detail-photo-delete"
              >
                <Trash2 className="h-3 w-3" /> Remove photo
              </button>
            )}
            <input
              ref={avatarFileRef}
              type="file"
              accept="image/png,.png"
              onChange={onAvatarFile}
              className="hidden"
              data-testid="member-detail-photo-file"
            />
          </div>
        </div>
      </Card>

      {/* Contact + about grid */}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Card className="p-5 rounded-2xl" data-testid="member-detail-contact-card">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Contact</div>
            {/* Editing here writes via /admin/members/{id}/profile — the
                same record the member's own portal reads from — so
                changes surface everywhere immediately. */}
            <Button
              variant="outline"
              size="sm"
              className="rounded-full h-7 px-3 text-xs"
              onClick={() => openEdit("contact")}
              data-testid="member-detail-contact-edit"
            >
              <Pencil className="h-3 w-3 mr-1.5" /> Edit
            </Button>
          </div>
          <InfoRow icon={Mail} label="Email" value={user.email} testid="member-detail-email" />
          <InfoRow icon={Phone} label="Phone" value={user.phone} testid="member-detail-phone" />
        </Card>
        <Card className="p-5 rounded-2xl" data-testid="member-detail-personal-card">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Personal</div>
            {/* Personal changes (DOB / gender / membership type) also
                flow through /admin/members/{id}/profile → the same
                record the member's own portal reads from. */}
            <Button
              variant="outline"
              size="sm"
              className="rounded-full h-7 px-3 text-xs"
              onClick={() => openEdit("personal")}
              data-testid="member-detail-personal-edit"
            >
              <Pencil className="h-3 w-3 mr-1.5" /> Edit
            </Button>
          </div>
          <InfoRow
            icon={Calendar}
            label="Date of birth"
            value={user.date_of_birth ? `${formatDate(user.date_of_birth)}${user.age != null ? ` · ${user.age} years` : ""}` : null}
            testid="member-detail-dob"
          />
          <InfoRow
            icon={UserIcon}
            label="Gender"
            value={user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : null}
            testid="member-detail-gender"
          />
          <InfoRow
            icon={ShieldCheck}
            label="Membership type"
            value={user.membership_type}
            testid="member-detail-type"
          />
        </Card>

        <Card className="p-5 rounded-2xl md:col-span-2" data-testid="member-detail-bio-card">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Bio</div>
            {/* Bio changes save via /admin/members/{id}/profile — the
                member's own portal reads from the same record so the
                update is instantly visible everywhere. */}
            <Button
              variant="outline"
              size="sm"
              className="rounded-full h-7 px-3 text-xs"
              onClick={() => openEdit("bio")}
              data-testid="member-detail-bio-edit"
            >
              <Pencil className="h-3 w-3 mr-1.5" /> Edit
            </Button>
          </div>
          <div className="py-3" data-testid="member-detail-bio">
            {user.bio
              ? <div className="text-sm whitespace-pre-wrap leading-relaxed">{user.bio}</div>
              : <div className="text-sm text-muted-foreground italic">This member hasn&rsquo;t added a bio yet.</div>}
          </div>
        </Card>

        {/* Career stats — auto from match_stat_lines with a manual
            override option. Only member managers / primary admin see
            the Edit button; everyone else reads the numbers. */}
        <Card className="p-5 rounded-2xl md:col-span-2" data-testid="member-detail-stats-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] flex items-center justify-center">
                <BarChart3 className="h-4 w-4" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Career stats</div>
                <div className="text-xs text-muted-foreground">Live totals from the club stats database. New PlayHQ appearances add to these numbers automatically.</div>
              </div>
            </div>
            {canEditStats && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() => setStatsEditOpen(true)}
                data-testid="member-detail-stats-edit"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
              </Button>
            )}
          </div>
          <MemberStatsGrid stats={stats} loading={statsLoading} />
        </Card>
      </div>

      <MemberEditDialog
        user={user}
        mode={editMode}
        open={editOpen}
        onOpenChange={(v) => { setEditOpen(v); if (!v) setEditMode(null); }}
        onSaved={(u) => { setUser(u); setEditOpen(false); setEditMode(null); }}
      />

      {canEditStats && (
        <StatsOverrideDialog
          open={statsEditOpen}
          onOpenChange={setStatsEditOpen}
          playerKey={playerKey}
          playerName={playerName}
          onSaved={() => loadStats(playerKey)}
        />
      )}
    </AppShell>
  );
}


// A single dialog serves every "Edit" pill on the member detail page.
// The ``mode`` prop scopes which fields are shown & submitted so admins
// can't accidentally edit unrelated bits of the profile from the wrong
// section:
//   • "contact"  → email + phone only
//   • "personal" → date-of-birth + gender + membership type
//   • "bio"      → bio only
//   • null       → full profile editor (legacy fallback)
// The payload only includes the fields visible in the current mode, so
// unchanged fields are never re-sent to the backend.
const MEMBERSHIP_TYPES = ["Player", "Non-Playing Member", "Life Member", "Junior", "Committee", "Coach", "Supporter"];

function MemberEditDialog({ user, mode, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setForm({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      phone: user.phone || "",
      bio: user.bio || "",
      date_of_birth: (user.date_of_birth || "").slice(0, 10),
      gender: user.gender || "",
      membership_type: user.membership_type || "",
    });
  }, [open, user]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Which keys are actually editable in this session — used to build
  // the outgoing payload so we never touch fields the admin can't see.
  const visibleKeys = (() => {
    switch (mode) {
      case "contact":  return ["email", "phone"];
      case "personal": return ["date_of_birth", "gender", "membership_type"];
      case "bio":      return ["bio"];
      default:         return ["first_name", "last_name", "email", "phone", "date_of_birth", "gender", "membership_type", "bio"];
    }
  })();

  const heading = (() => {
    switch (mode) {
      case "contact":  return "Edit contact details";
      case "personal": return "Edit personal details";
      case "bio":      return "Edit bio";
      default:         return `Edit ${user?.first_name || ""} ${user?.last_name || ""}`.trim();
    }
  })();

  const submit = async () => {
    if (!user) return;
    setSaving(true);
    try {
      // Only send the fields visible in the current mode so we never
      // clobber unrelated values.
      const payload = {};
      for (const k of visibleKeys) payload[k] = form[k];
      if (!payload.gender) delete payload.gender;
      if (!payload.date_of_birth) delete payload.date_of_birth;
      if (payload.membership_type === "") delete payload.membership_type;
      const { data } = await api.put(`/admin/members/${user.id}/profile`, payload);
      // Refresh the full record including age via the read endpoint
      const { data: full } = await api.get(`/members/${user.id}`);
      onSaved(full || data);
      toast.success(`${full?.first_name || "Profile"} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const show = (k) => visibleKeys.includes(k);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="member-edit-dialog">
        <DialogHeader>
          <DialogTitle data-testid="member-edit-title">{heading}</DialogTitle>
          <DialogDescription>
            Changes here are saved to the member&rsquo;s profile and shown in both the admin
            members view and the member&rsquo;s own portal.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {show("first_name") && (
            <div>
              <Label className="text-xs">First name</Label>
              <Input value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)} data-testid="member-edit-first-name" />
            </div>
          )}
          {show("last_name") && (
            <div>
              <Label className="text-xs">Last name</Label>
              <Input value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} data-testid="member-edit-last-name" />
            </div>
          )}
          {show("email") && (
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="member-edit-email" />
            </div>
          )}
          {show("phone") && (
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="member-edit-phone" />
            </div>
          )}
          {show("date_of_birth") && (
            <div>
              <Label className="text-xs">Date of birth</Label>
              <Input type="date" value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="member-edit-dob" />
            </div>
          )}
          {show("gender") && (
            <div>
              <Label className="text-xs">Gender</Label>
              <Select value={form.gender || ""} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger data-testid="member-edit-gender"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male" data-testid="member-edit-gender-male">Male</SelectItem>
                  <SelectItem value="female" data-testid="member-edit-gender-female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {show("membership_type") && (
            <div className="sm:col-span-2">
              <Label className="text-xs">Membership type</Label>
              <Select value={form.membership_type || ""} onValueChange={(v) => set("membership_type", v)}>
                <SelectTrigger data-testid="member-edit-membership-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {MEMBERSHIP_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`member-edit-membership-${t.replace(/\s+/g, "-").toLowerCase()}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {show("bio") && (
            <div className="sm:col-span-2">
              <Label className="text-xs">Bio</Label>
              <Textarea rows={4} value={form.bio || ""} onChange={(e) => set("bio", e.target.value)} data-testid="member-edit-bio" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="member-edit-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// PlayHQ id editor — manual input + one-tap "suggest matches" from PlayHQ
// data already synced into the app. Admin-only card on the member detail
// page. Suggestions are ranked by how many appearances that PlayHQ player
// has in our synced game summaries — the noisier the surface, the higher
// the confidence.
// ---------------------------------------------------------------------------
function PlayHQIdEditor({ user, onSaved }) {
  const [value, setValue] = useState(user.playhq_id || "");
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSug, setLoadingSug] = useState(false);

  const save = async (newVal) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/members/${user.id}/playhq-id`, { playhq_id: newVal });
      onSaved?.(data.playhq_id || "");
      setValue(data.playhq_id || "");
      toast.success(newVal ? "PlayHQ id linked" : "PlayHQ id cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const suggest = async () => {
    setLoadingSug(true);
    setSuggestions(null);
    try {
      const { data } = await api.get(`/admin/members/${user.id}/playhq-matches`);
      setSuggestions(data.candidates || []);
    } catch (e) {
      toast.error("Failed to search PlayHQ data");
    } finally { setLoadingSug(false); }
  };

  return (
    <div className="py-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Paste a PlayHQ player id, or use Suggest matches"
          className="max-w-md font-mono-sm"
          data-testid="member-playhq-id-input"
        />
        <Button
          onClick={() => save(value.trim())}
          disabled={saving || value.trim() === (user.playhq_id || "")}
          className="rounded-full"
          data-testid="member-playhq-id-save"
        >Save</Button>
        {user.playhq_id && (
          <Button
            variant="outline"
            onClick={() => save("")}
            disabled={saving}
            className="rounded-full"
            data-testid="member-playhq-id-clear"
          >Clear</Button>
        )}
        <Button
          variant="secondary"
          onClick={suggest}
          disabled={loadingSug}
          className="rounded-full"
          data-testid="member-playhq-id-suggest"
        >
          {loadingSug ? "Searching…" : "Suggest matches"}
        </Button>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        Visible only to admins. When set, this member&rsquo;s appearances in Results are linked to this PlayHQ player id.
      </div>
      {suggestions !== null && (
        <div className="mt-4">
          {suggestions.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No candidates found in synced PlayHQ data.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2" data-testid="member-playhq-suggest-list">
              {suggestions.map((c) => (
                <button
                  key={c.playhq_id}
                  type="button"
                  onClick={() => save(c.playhq_id)}
                  className="text-left flex items-center justify-between gap-3 rounded-xl border border-border bg-[hsl(var(--secondary))]/40 px-3 py-2 hover:border-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/5 transition-colors"
                  data-testid={`member-playhq-suggest-${c.playhq_id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-[10px] font-mono text-muted-foreground truncate">{c.playhq_id}</div>
                  </div>
                  <span className="shrink-0 text-[11px] rounded-full bg-[hsl(var(--accent))] text-white px-2 py-0.5">{c.count} apps</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * MemberStatsGrid — read-only career-stats display used on the member
 * detail page. Falls back to zero / "—" for missing fields so unlinked
 * members still render a full grid. Bowling strike rate is derived
 * client-side from ``overs`` and ``wickets``.
 */
function MemberStatsGrid({ stats, loading }) {
  const s = stats || {};

  const fmt = (v, decimals = 0) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isNaN(n)) return String(v);
    return decimals ? n.toFixed(decimals) : String(Math.round(n));
  };
  const fmtBallsPerWicket = () => {
    const oversStr = s.overs;
    const wickets = Number(s.wickets || 0);
    if (!wickets || oversStr == null) return "—";
    const [whole, decimalBalls] = String(oversStr).split(".");
    const balls = Number(whole || 0) * 6 + Number(decimalBalls || 0);
    if (!balls) return "—";
    return (balls / wickets).toFixed(2);
  };

  const batting = [
    ["Matches", fmt(s.matches)],
    ["Innings", fmt(s.innings_batted)],
    ["Not outs", fmt(s.not_outs)],
    ["Highest", s.highest ?? "—"],
    ["Ducks", fmt(s.ducks)],
    ["Total runs", fmt(s.runs)],
    ["100s", fmt(s.centuries)],
    ["50s", fmt(s.fifties)],
    ["Average", fmt(s.batting_average, 2)],
  ];
  const fielding = [
    ["Total catches", fmt(s.catches)],
    ["Wkp catches", fmt(s.wk_catches)],
    ["Non-wkp catches", fmt(s.non_wk_catches)],
    ["Run outs", fmt(s.run_outs)],
  ];
  const bowling = [
    ["Total wickets", fmt(s.wickets)],
    ["Overs bowled", s.overs ?? "—"],
    ["Best bowling", s.best_bowling ?? "—"],
    ["Runs conceded", fmt(s.runs_conceded)],
    ["Average", fmt(s.bowling_average, 2)],
    ["Strike rate", fmtBallsPerWicket()],
  ];

  if (loading) {
    return <div className="text-sm text-muted-foreground py-4">Loading stats…</div>;
  }
  if (!stats) {
    return (
      <div className="text-sm text-muted-foreground py-4 italic">
        No stats recorded yet — they&rsquo;ll appear here after their first PlayHQ appearance.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="member-detail-stats-grid">
      <StatsSection title="Batting" cells={batting} />
      <StatsSection title="Fielding" cells={fielding} />
      <StatsSection title="Bowling" cells={bowling} />
    </div>
  );
}

function StatsSection({ title, cells }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-2">{title}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {cells.map(([label, value]) => (
          <div key={label} className="rounded-lg border border-border p-2.5 bg-[hsl(var(--secondary))]/40">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-base font-semibold tabular-nums mt-0.5">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
